import { Column, Index } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';

/**
 * PD-17294 — which of an item's money fields a history row records. List
 * price, and both cost fields, because tenants (and price contracts' cost
 * source) use either: acquisitionCost ("Acquisition Cost") or sellCost (the
 * item editor's "Unit Cost").
 */
export type ItemPriceHistoryField = 'price' | 'acquisitionCost' | 'sellCost';

/** The item fields whose every change is kept, in display order. */
export const ITEM_PRICE_HISTORY_FIELDS: ReadonlyArray<ItemPriceHistoryField> = [
	'price',
	'acquisitionCost',
	'sellCost',
];

/**
 * PD-17294 — where a recorded change came from:
 *  - 'workd'    an edit saved in Workd (the creator user is who made it)
 *  - 'import'   an ERP import (sourceName names it, e.g. "Prophet 21")
 *  - 'seed'     the item's last change as it stood when history began
 *               (item.previousPrice → price at priceModifiedDatetime)
 *  - 'backfill' copied from an older record of changes (Pharmsource's
 *               item_cost_change_receipt)
 */
export type ItemPriceHistorySource = 'workd' | 'import' | 'seed' | 'backfill';

/**
 * PD-17294 — every change to an item's list price or cost, kept from now on
 * (the item itself keeps only its latest change, previousPrice and
 * priceModifiedDatetime). Written by ItemPriceTrackingSubscriber for edits
 * in Workd and by each ERP import for the changes it brings in (PD-17295,
 * integration side).
 *
 * auditable: false — the row IS the record of the change; auditing it would
 * write a second copy of the same fact to _audit.
 */
@Index('idx_item_price_history_item_field_date', ['fkItemId', 'field', 'changedDate'])
@Index('idx_item_price_history_changed_date', ['changedDate'])
@Entity('item_price_history', {}, { auditable: false })
export class ItemPriceHistoryEntity extends BaseEntity {
	@Column('int', { name: 'fk_item__id', unsigned: true })
	public fkItemId!: number;

	@Column('varchar', { name: 'field', length: 32 })
	public field!: ItemPriceHistoryField;

	/** The value before the change; null when the field had none. */
	@Column('decimal', { name: 'previousValue', nullable: true, precision: 15, scale: 6 })
	public previousValue!: string | null;

	/** The value after the change; null when it was cleared. */
	@Column('decimal', { name: 'newValue', nullable: true, precision: 15, scale: 6 })
	public newValue!: string | null;

	/** When the change took effect (for an import, when it was imported). */
	@Column('datetime', { name: 'changedDate' })
	public changedDate!: Date;

	@Column('varchar', { name: 'source', length: 16 })
	public source!: ItemPriceHistorySource;

	/** The import's name for source 'import' ("Prophet 21", "Business Edge"); otherwise null. */
	@Column('varchar', { name: 'sourceName', nullable: true, length: 64 })
	public sourceName!: string | null;
}
